import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { PlaceModel } from "common247";
import { useFirebase } from "@/components/firebase/useFirebase";
import { getMyPlaces } from "@/services/firebase/place-functions";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the PlaceSelector popup component.
 */
export interface IPlaceSelectorProps {
  /** Whether the place selector popup is visible */
  visible: boolean;
  /** Callback fired when the selector is dismissed */
  onClose: () => void;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Single place selection callback */
  onSelectPlace?: (place: PlaceModel) => void;
  /** Multi-place selection callback */
  onSelectMultiplePlaces?: (places: PlaceModel[]) => void;
  /** Whether multi-select is enabled (default: false) */
  multiSelect?: boolean;
  /** Pre-selected place IDs */
  selectedPlaceIds?: string[];
  /** Optional pre-fetched places */
  places?: PlaceModel[];
  /** Optional title override (default: "Select Place") */
  title?: string;
  /** Optional subtitle or instructions */
  subtitle?: string;
  /** Target width for the popover card (default: 440) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID for testing */
  testID?: string;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Contextual PlaceSelector powered by ThemedPopupScreen.
 * Provides searchable place selection anchored near the user cursor/trigger.
 */
export function PlaceSelector({
  visible,
  onClose,
  anchorPosition,
  onSelectPlace,
  onSelectMultiplePlaces,
  multiSelect = false,
  selectedPlaceIds = DEFAULT_EMPTY_IDS,
  places: propPlaces,
  title = "Select Place",
  subtitle,
  width = 440,
  maxHeight = 500,
  testID = "place-selector",
}: IPlaceSelectorProps) {
  const services = useFirebase();
  const [internalPlaces, setInternalPlaces] = useState<PlaceModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedPlaceIds);

  const { selector: selectorTheme, palette } = useComponentTheme();
  const cardBg = palette.card;
  const borderColor = selectorTheme.row.restingBorderColor;
  const hoverBorderColor = selectorTheme.row.hoverBorderColor;
  const tintColor = selectorTheme.row.selectedBorderColor;
  const textMuted = selectorTheme.row.subtitleColor;
  const badgeBg = selectorTheme.avatarBadge.backgroundColor;

  useEffect(() => {
    setSelectedIds((prev) => {
      if (
        prev.length === selectedPlaceIds.length &&
        prev.every((id, idx) => id === selectedPlaceIds[idx])
      ) {
        return prev;
      }
      return selectedPlaceIds;
    });
  }, [selectedPlaceIds, visible]);

  useEffect(() => {
    if (!visible) return;
    if (propPlaces && propPlaces.length > 0) {
      setInternalPlaces(propPlaces);
      return;
    }
    let isMounted = true;
    const fetchPlaces = async () => {
      setLoading(true);
      try {
        const res = await getMyPlaces(services, {});
        if (isMounted && res.status && res.places) {
          setInternalPlaces(res.places);
        }
      } catch (err) {
        console.error("[PlaceSelector] Failed to load places:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPlaces();
    return () => {
      isMounted = false;
    };
  }, [visible, propPlaces, services]);

  const filteredPlaces = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return internalPlaces;
    return internalPlaces.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const desc = (p.description || "").toLowerCase();
      const addr = (p.address || "").toLowerCase();
      return name.includes(q) || desc.includes(q) || addr.includes(q);
    });
  }, [internalPlaces, searchQuery]);

  const handleTogglePlace = (place: PlaceModel) => {
    const id = place.id;
    if (!id) return;

    if (!multiSelect) {
      onSelectPlace?.(place);
      onClose();
      return;
    }

    const nextSelected = selectedIds.includes(id)
      ? selectedIds.filter((item) => item !== id)
      : [...selectedIds, id];
    setSelectedIds(nextSelected);

    if (onSelectMultiplePlaces) {
      const selectedModels = internalPlaces.filter((p) =>
        nextSelected.includes(p.id || "")
      );
      onSelectMultiplePlaces(selectedModels);
    }
  };

  const computedSubtitle =
    subtitle ??
    (multiSelect
      ? `${selectedIds.length} place${selectedIds.length === 1 ? "" : "s"} selected`
      : undefined);

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={title}
      subtitle={computedSubtitle}
      icon="location-outline"
      width={width}
      maxHeight={maxHeight}
      testID={testID}
      footer={
        multiSelect ? (
          <View style={styles.footerRow}>
            <ThemedButton
              title="Done"
              variant="primary"
              compact
              onPress={onClose}
            />
          </View>
        ) : undefined
      }
    >
      <View style={styles.container}>
        <ThemedInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search places by name or address..."
          style={styles.searchInput}
          clearButtonMode="while-editing"
          autoFocus={false}
        />

        {loading ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="small" color={tintColor} />
            <ThemedText style={[styles.loadingText, { color: textMuted }]}>
              Loading places...
            </ThemedText>
          </View>
        ) : filteredPlaces.length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons name="location-outline" size={32} color={textMuted} />
            <ThemedText style={[styles.emptyText, { color: textMuted }]}>
              {searchQuery ? "No places match your search." : "No places found."}
            </ThemedText>
          </View>
        ) : (
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.listContent}
            keyboardShouldPersistTaps="handled"
          >
            {filteredPlaces.map((place) => {
              const id = place.id;
              const isSelected = selectedIds.includes(id);

              return (
                <TouchableOpacity
                  key={`place-option-${id}`}
                  style={[
                    styles.row,
                    {
                      backgroundColor: isSelected ? `${tintColor}14` : cardBg,
                      borderColor: isSelected ? tintColor : borderColor,
                    },
                  ]}
                  onPress={() => handleTogglePlace(place)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                >
                  <View style={[styles.avatar, { backgroundColor: badgeBg }]}>
                    <Ionicons name="location" size={18} color="#7E22CE" />
                  </View>

                  <View style={styles.rowDetails}>
                    <ThemedText numberOfLines={1} type="defaultSemiBold">
                      {place.name || "Untitled Place"}
                    </ThemedText>
                    {place.address ? (
                      <ThemedText numberOfLines={1} style={[styles.addressText, { color: textMuted }]}>
                        📍 {place.address}
                      </ThemedText>
                    ) : place.latitude !== undefined && place.longitude !== undefined ? (
                      <ThemedText numberOfLines={1} style={[styles.addressText, { color: textMuted }]}>
                        🌐 {place.latitude.toFixed(4)}, {place.longitude.toFixed(4)}
                      </ThemedText>
                    ) : null}
                  </View>

                  {multiSelect ? (
                    <Ionicons
                      name={isSelected ? "checkbox" : "square-outline"}
                      size={20}
                      color={isSelected ? tintColor : textMuted}
                    />
                  ) : (
                    <Ionicons name="chevron-forward" size={16} color={textMuted} />
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  searchInput: {
    marginBottom: 10,
  },
  list: {
    flex: 1,
  },
  listContent: {
    gap: 8,
    paddingBottom: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 10,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: "center",
    justifyContent: "center",
  },
  rowDetails: {
    flex: 1,
    gap: 2,
  },
  addressText: {
    fontSize: 12,
  },
  centerContainer: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
  },
  emptyText: {
    fontSize: 14,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingTop: 8,
  },
});
